import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <main className="grid min-h-dvh place-items-center px-6 py-24 sm:py-32 lg:py-8">
          <div className="text-center">
            <p className="text-base font-semibold text-green-900">
              Something went wrong
            </p>
            <h1 className="mt-4 text-3xl font-bold text-gray-900 sm:text-5xl">
              Unexpected error
            </h1>
            <p className="mt-6 text-base leading-7 text-gray-600">
              We’re sorry. Something went wrong on our end. Try refreshing the
              page or go back home.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-md bg-green-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-900"
              >
                Refresh page
              </button>
              <Link
                to="/"
                className="text-sm font-semibold text-green-900"
              >
                Go back home <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}
